import * as React from 'react';
import { ChangeEvent, SVGProps } from 'react';
import { TextInput as FlowBiteTextInput } from 'flowbite-react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@restworld/utils-common';

export type EmailInputVariant = 'primary' | 'error';

export interface EmailInputProps {
  value?: string;
  label?: string | React.ReactNode;
  supportText?: string | React.ReactNode;
  variant?: EmailInputVariant;
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  containerStyles?: string[];
  inputStyles?: string[];
  labelStyles?: string[];
  supportTextStyles?: string;
  suffixIcon?: React.FC<SVGProps<SVGSVGElement>>;
  placeholder?: string;
  disabled?: boolean;
  testId?: string;
  supportTextTestId?: string;
  id?: string;
  onBlur?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export function isValidEmail(email: string) {
  const emailPattern = /^\S+@\S+\.\S+$/;
  return emailPattern.test(email);
}

export const EmailInput = ({
  value,
  label,
  supportText,
  variant = 'primary',
  onChange,
  containerStyles,
  inputStyles,
  labelStyles,
  supportTextStyles,
  suffixIcon,
  placeholder,
  disabled,
  testId,
  supportTextTestId,
  id,
  onBlur,
}: EmailInputProps) => {
  const isErrored = variant === 'error';
  const errorMessage = supportText || "L'email inserita non è valida, ricontrolla e riprova";
  return (
    <StyledContainer containerStyles={containerStyles}>
      {label && <StyledLabel labelStyles={labelStyles}>{label}</StyledLabel>}
      <TextField
        onChange={onChange}
        onBlur={onBlur}
        value={value}
        variant={variant}
        inputStyles={inputStyles}
        rightIcon={(isErrored && ErrorIcon) || (suffixIcon && suffixIcon) || undefined}
        placeholder={placeholder}
        disabled={disabled}
        data-cy={testId}
        id={id}
      />
      {(supportText || errorMessage) && (
        <StyledSupportText variant={variant} supportTextStyles={supportTextStyles} data-cy={supportTextTestId}>
          {isErrored ? errorMessage : supportText}
        </StyledSupportText>
      )}
    </StyledContainer>
  );
};

const StyledContainer = ({ containerStyles, className, ...props }: any) => (
  <div className={cn('flex flex-col', className)} {...props} />
);

const StyledLabel = ({ labelStyles, className, ...props }: any) => (
  <p className={cn('text-base font-semibold leading-6 mb-2', className)} {...props} />
);

const TextField = ({ variant, inputStyles, className, ...props }: any) => (
  <FlowBiteTextInput
    className={cn(
      'flex flex-1 !bg-white',
      '[&_input]:w-full [&_input]:font-normal [&_input]:outline-none [&_input]:focus:outline-none [&_input]:border [&_input]:border-solid [&_input]:border-gray-200 [&_input]:focus:border-gray-200 [&_input]:text-base [&_input]:text-gray-500 [&_input]:leading-5 [&_input]:!bg-white [&_input]:px-4 [&_input]:pr-3 [&_input]:rounded-lg',
      variant === 'primary' && '[&_input]:placeholder:text-gray-400',
      variant === 'error' &&
        '[&_input]:!bg-red-50 [&_input]:placeholder:text-red-700 [&_input]:text-red-700 [&_input]:border-red-500',
      className
    )}
    {...props}
  />
);

const StyledSupportText = ({ supportTextStyles, variant, className, ...props }: any) => (
  <p
    className={cn(
      'text-sm text-gray-500 font-normal leading-5.25 mt-3',
      variant === 'error' && 'text-red-600',
      className
    )}
    {...props}
  />
);

const ErrorIcon = ({ className, ...props }: any) => (
  <ExclamationCircleIcon className={cn('text-red-700', className)} {...props} />
);
